<!-- =========================================================================================
    File Name: PopupBuscarPersona.vue
    Description: Popup que busca unha persoa na BD, así como teléfonos e e-mails de contacto e as direccións
    Component Name: VxCard
    ----------------------------------------------------------------------------------------
    Author: DIATOR
    ----------------------------------------------------------------------------------------
    Cando se instancia o compoñente debe recibir por 'props' o obxecto 'popupProps' coas seguintes propiedades:
      - popupProps.activo (boolean, para mostrar o pupup ou pechalo)
      - popupProps.dni (string, DNI a buscar na BD)
      - popupProps.relacion (string, tipo de relación da persoa buscada coa operación: 'CLIENTE', 'DECISOR' ou 'TITULAR')
      - popupProps.origenLlamadaPopUp (string, para saber se se chama desde a alta dunha operación -NUEVA- ou desde outro lugar. Na alta, cando se
          abre o popup para buscar un CLIENTE, na solapa de 'Direcciones' hai que mostrar os checkbox para indicar se queremos gardar unha das 
          direccións como dirección de instalación, de facturación, ou ambas. Sen embargo cando se chama desde a modificación dunha operación 
          desde a solapa de 'Cliente', non hai que mostrar ditos checkbox e a dirección que se pode seleccionar vai ser sempre a dirección de 
          facturación, ou sexa a dirección do cliente).

    O compoñente devolve a través dun evento personailzado o obxecto 'datosSeleccionados', que se crea no 
    método 'crearDatosSeleccionados()'.
========================================================================================== -->

<template>
  <div>
    <vs-popup title="Buscar persona" :active.sync="popupProps.activo" button-close-hidden>

      <div class="vx-row mb-2">
        <div class="vx-col w-full">
          <span>Resultado de la búsqueda por el DNI/CIF: </span><strong>{{ popupProps.dni }}</strong>
        </div>
      </div>

      <vs-divider />

      <div v-if="existe">
        <vs-tabs>

          <vs-tab tag="persona" label="Persona" icon-pack="feather" icon="icon-user">
            <vs-divider />
            <div class="vx-row">
              <div class="vx-col sm:w-5/12 w-full">
                <span><strong>Nombre o razón social:</strong></span>
              </div>
              <div class="vx-col sm:w-7/12 w-full">
                <span>{{ persona.nombre }} {{ persona.apellidos }}</span>
              </div>
            </div>
            <div class="vx-row">
              <div class="vx-col sm:w-5/12 w-full">
                <span><strong>Fecha de nacimiento:</strong></span>
              </div>
              <div class="vx-col sm:w-7/12 w-full">
                <span>{{ persona.fechaNacimiento | date }}</span>
              </div>
            </div>
          </vs-tab>

          <div v-if="popupProps.relacion === 'CLIENTE' || popupProps.relacion === 'DECISOR'">
            <vs-tab tag="movil" label="Móvil" icon-pack="feather" icon="icon-phone">
              <vs-divider />
              <div class="vx-row">
                <div class="vx-col sm:w-1/12 w-full">
                  <vs-radio vs-name="movil" v-model="movilSeleccionado" :vs-value="99999999"></vs-radio>
                </div>
                <div class="vx-col sm:w-11/12 w-full">
                  <span>Introducir nuevo móvil</span>
                </div>
                <vs-divider />
              </div>
              <div class="vx-row" v-for="(item, index) in persona.telefonos" :key="index">
                <div class="vx-col sm:w-1/12 w-full">
                  <vs-radio vs-name="movil" v-model="movilSeleccionado" :vs-value="index"></vs-radio>
                </div>
                <div class="vx-col sm:w-5/12 w-full">
                  <span>{{ item }}</span>
                </div>
                <vs-divider />
              </div>
            </vs-tab>
          </div>

          <div v-if="popupProps.relacion === 'CLIENTE' || popupProps.relacion === 'DECISOR'">
            <vs-tab tag="email" label="E-mail" icon-pack="feather" icon="icon-mail">
              <vs-divider />
              <div class="vx-row">
                <div class="vx-col sm:w-1/12 w-full">
                  <vs-radio vs-name="email" v-model="emailSeleccionado" :vs-value="99999999"></vs-radio>
                </div>
                <div class="vx-col sm:w-11/12 w-full">
                  <span>Introducir nuevo e-mail</span>
                </div>
                <vs-divider />
              </div>
              <div class="vx-row" v-for="(item, index) in persona.emails" :key="index">
                <div class="vx-col sm:w-1/12 w-full">
                  <vs-radio vs-name="email" v-model="emailSeleccionado" :vs-value="index"></vs-radio>
                </div>
                <div class="vx-col sm:w-5/12 w-full">
                  <span>{{ item }}</span>
                </div>
                <vs-divider />
              </div>
            </vs-tab>
          </div>

          <div v-if="popupProps.relacion === 'CLIENTE'">
            <vs-tab tag="direccion" label="Direcciones" icon-pack="feather" icon="icon-mail">
              <vs-divider />
              <div class="vx-row">
                <div class="vx-col sm:w-1/12 w-full">
                  <vs-radio v-model="direccionSeleccionada" :vs-value="99999999"></vs-radio>
                </div>
                <div class="vx-col sm:w-11/12 w-full">
                  <span>Introducir nueva dirección</span>
                </div>
                <vs-divider />
              </div>
              <div class="vx-row" v-for="(item, index) in persona.direcciones" :key="index">
                <div class="vx-col sm:w-1/12 w-full">
                  <vs-radio v-model="direccionSeleccionada" :vs-value="index"></vs-radio>
                </div>
                <div class="vx-col sm:w-8/12 w-full">
                  <span>{{ item.direccion }}, </span>
                  <span v-if="item.numero !== undefined">Nº {{ item.numero }}, </span>
                  <span v-if="item.portal !== undefined">Portal {{ item.portal }}, </span>
                  <span v-if="item.escalera !== undefined">Escalera {{ item.escalera }}, </span>
                  <span v-if="item.planta !== undefined">Planta {{ item.planta }}, </span>
                  <span v-if="item.puerta !== undefined">Puerta {{ item.puerta }}</span>
                  <br>
                  <span v-if="item.localidad !== undefined">{{ item.localidad }}, </span>
                  <span>{{ item.municipio.nombre }}</span>
                  <br>
                  <span>{{ item.cp }} </span>
                  <span>{{ item.provincia.nombre }}</span>
                  <span v-if="item.pais !== undefined"> ({{ item.pais }})</span>
                </div>
                <div class="vx-col sm:w-3/12 w-full">
                  <!-- Solo mostramos os checkbox cando chamamos ao popup desde a pantalla de nova operación -->
                  <!-- Se chamamos desde a modificación dunha operación a dirección sempre vai ser a de facturación, non pode ser a de instalación -->
                  <div class="vx-row" v-if="popupProps.origenLlamadaPopUp === 'NUEVA'">  
                    <vs-checkbox v-model="checkInstalacion[index]" :disabled="checkboxDisabled[index]">Instalación</vs-checkbox>
                  </div>
                  <!-- Solo mostramos os checkbox cando chamamos ao popup desde a pantalla de nova operación -->
                  <!-- Se chamamos desde a modificación dunha operación a dirección sempre vai ser a de facturación, non pode ser a de instalación -->
                  <div class="vx-row" v-if="popupProps.origenLlamadaPopUp === 'NUEVA'">
                    <vs-checkbox v-model="checkFacturacion[index]" :disabled="checkboxDisabled[index]">Facturación</vs-checkbox>
                  </div>
                </div>
                <vs-divider />
              </div>
            </vs-tab>
          </div>

        </vs-tabs>
      </div>

      <div v-else>
        <div class="vx-row">
          <div class="vx-col">
            <span>No se ha encontrado ninguna persona con ese DNI/CIF. Es necesario darla de alta.</span>
          </div>
        </div>
      </div>

      <vs-divider />

      <div class="vx-col w-full flex justify-end">
        <vs-button @click="aceptar" color="primary" type="filled" vs-justify="flex-end">Aceptar</vs-button>
      </div>
    </vs-popup>
  </div>
</template>

<script>
import commonComponentsStore from '@/store/common-components/index.js'

export default {
  name: 'PopupBuscarPersona',
  components: {

  },
  props: {
    popupProps: {}
  },
  data () {
    return {
      persona: {},
      existe: false,
      movilSeleccionado: 99999999,
      emailSeleccionado: 99999999,
      direccionSeleccionada: 99999999,
      datosSeleccionados: {},
      checkInstalacion: [],
      checkFacturacion: [],
      checkboxDisabled: []
    }
  },
  watch: {
    direccionSeleccionada (id) {
      this.estadoCheckboxInstalacionFacturacion(id)
    }
  },
  methods: {
    aceptar () {
      this.crearDatosSeleccionados()
      this.$emit('aceptar', this.datosSeleccionados)
    }, 
    async consultarDni () {
      // Reseteamos os valores das propiedades definitas en 'data ()' posto que non se reinician ao pechar o popup. 
      // Como este compoñente se chama dende tres botóns distintos na mesma pantalla, é necesario resetear estas propiedades a man, 
      // posto que se non se fai así cando se volve chamar ao popup desde outro botón mantén os datos da consulta anterior.
      this.reiniciarPropiedades()
      this.$vs.loading('material') // Mostramos unha animación de 'cargando...'
      await this.$store.dispatch('commonComponentsStore/getPersona', this.popupProps.dni)
        .then((response) => {
          if (response.length > 0) {
            this.persona = this.eliminarDuplicados(response[0])
            this.estadoCheckboxInstalacionFacturacion(null)
            this.existe = true
          }
          this.$vs.loading.close()  // Pechamos a animación de 'cargando...'
          this.popupProps.activo = true
        })
        .catch((error) => {
          console.log(error)
        })
    },
    reiniciarPropiedades () {
      this.persona = {
        direcciones: []
      }
      this.existe = false
      this.movilSeleccionado = 99999999
      this.emailSeleccionado = 99999999
      this.direccionSeleccionada = 99999999
      this.datosSeleccionados = {}
    },
    estadoCheckboxInstalacionFacturacion (id) {
      const elementos = this.persona.direcciones.length
      for (let i = 0; i < elementos; i++) {
        if (i === id) {
          this.checkboxDisabled[i] = false
        } else {
          this.checkInstalacion[i] = false
          this.checkFacturacion[i] = false
          this.checkboxDisabled[i] = true
        }
      }   
    },
    eliminarDuplicados (persona) {  // Función para eliminar duplicados en teléfonos, e-mails e direccións
      const op = persona.operacionPersonas
      const pd = JSON.parse(JSON.stringify(persona.direcciones))
      const d = []  // Creamos un array vacío para utilizalo na eliminación de direccións duplicadas
      persona.operacionPersonas = []  // Vaciamos operación persoas porque xa non se vai usar
      persona.telefonos = []  // Creamos un array para gardar os teléfonos sen repetidos
      persona.emails = []  // Creamos un array para gardar os e-mails sen repetidos

      // Eliminamos duplicados en teléfonos e e-mails 
      op.forEach(element => {
        if (element.telefono !== undefined && element.telefono !== null && !persona.telefonos.includes(element.telefono)) {
          persona.telefonos.push(element.telefono)
        }
        if (element.email !== undefined && element.email !== null && !persona.emails.includes(element.email)) {
          persona.emails.push(element.email)
        }
      })

      // Eliminamos duplicados en direccións
      pd.forEach(element => {
        const dir = element.direccion + element.numero + element.portal + element.escalera + element.planta + element.puerta + 
          element.localidad + element.municipio.nombre + element.cp + element.provincia.nombre + element.pais
          
        if (d.includes(dir)) {  // Se a dirección xa existe borramos a dirección do array orixinal
          const idx = persona.direcciones.findIndex(el => {
            const dir1 = el.direccion + el.numero + el.portal + el.escalera + el.planta + el.puerta + 
              el.localidad + el.municipio.nombre + el.cp + el.provincia.nombre + el.pais
            return dir1 === dir
          })
          persona.direcciones.splice(idx, 1)
        } else {
          d.push(dir)  // A dirección non existía, polo tanto gardamos o string da dirección completa para comparalo coas seguintes direccións
        }
      })

      return persona
    },
    crearDatosSeleccionados () {
      this.datosSeleccionados.idProducto = this.popupProps.idProducto
      this.datosSeleccionados.tipoPersona = this.popupProps.relacion
      this.datosSeleccionados.existe = this.existe
      this.datosSeleccionados.index = this.popupProps.index
      this.datosSeleccionados.indexPrincipal = this.popupProps.indexPrincipal
      // Se a persoa existe na BD creamos o obxecto cos datos seleccionados
      if (this.existe) {        
        this.datosSeleccionados.personaId = this.persona.id
        this.datosSeleccionados.nombre = this.persona.nombre
        this.datosSeleccionados.apellidos = this.persona.apellidos
        this.datosSeleccionados.fechaNacimiento = this.persona.fechaNacimiento
        if (this.popupProps.relacion === 'CLIENTE' || 'DECISOR') {
          if (this.movilSeleccionado === 99999999) {
            this.datosSeleccionados.movil = 'NUEVO'
          } else {
            this.datosSeleccionados.movil = this.persona.telefonos[this.movilSeleccionado]
          }
          if (this.emailSeleccionado === 99999999) {
            this.datosSeleccionados.email = 'NUEVO'
          } else {
            this.datosSeleccionados.email = this.persona.emails[this.emailSeleccionado]
          }
        }
        if (this.popupProps.relacion === 'CLIENTE') {
          if (this.direccionSeleccionada === 99999999) {
            this.datosSeleccionados.direccion = 'NUEVA'
          } else {
            this.datosSeleccionados.direccionId = this.persona.direcciones[this.direccionSeleccionada].id
            this.datosSeleccionados.direccion = this.persona.direcciones[this.direccionSeleccionada].direccion
            this.datosSeleccionados.numero = this.persona.direcciones[this.direccionSeleccionada].numero
            this.datosSeleccionados.portal = this.persona.direcciones[this.direccionSeleccionada].portal
            this.datosSeleccionados.escalera = this.persona.direcciones[this.direccionSeleccionada].escalera
            this.datosSeleccionados.planta = this.persona.direcciones[this.direccionSeleccionada].planta
            this.datosSeleccionados.puerta = this.persona.direcciones[this.direccionSeleccionada].puerta
            this.datosSeleccionados.localidad = this.persona.direcciones[this.direccionSeleccionada].localidad
            this.datosSeleccionados.cp = this.persona.direcciones[this.direccionSeleccionada].cp
            this.datosSeleccionados.municipioId = this.persona.direcciones[this.direccionSeleccionada].municipio.id
            this.datosSeleccionados.municipioNombre = this.persona.direcciones[this.direccionSeleccionada].municipio.nombre
            this.datosSeleccionados.provinciaId = this.persona.direcciones[this.direccionSeleccionada].provincia.id
            this.datosSeleccionados.provinciaNombre = this.persona.direcciones[this.direccionSeleccionada].provincia.nombre
            this.datosSeleccionados.pais = this.persona.direcciones[this.direccionSeleccionada].pais
            this.checkInstalacion[this.direccionSeleccionada] ? this.datosSeleccionados.instalacion = 'S' : this.datosSeleccionados.instalacion = 'N'
            this.checkFacturacion[this.direccionSeleccionada] ? this.datosSeleccionados.facturacion = 'S' : this.datosSeleccionados.facturacion = 'N'
          }
        }
      }
    }
  },
  async created () {
    if (!commonComponentsStore.isRegistered) {
      this.$store.registerModule('commonComponentsStore', commonComponentsStore)
      commonComponentsStore.isRegistered = true
    }
  }
}
</script>

<style scoped>

</style>